import { useCallback, useEffect, useMemo, useState } from "react";
import {
  GetCompanyNewsRequest,
  GetCompanyNewsResponse,
  PublishedRange,
} from "../models/newsModels";
import { usePost } from "./usePost";
import { useToast } from "../store/ToastProvider";
import { MESSAGES } from "../messages";

type UseCompanyNewsParams = {
  compassCids: string[] | null;
  portfolioIndexIds: string[] | null;
  days: number;
  publishedRange?: PublishedRange | null;
  limit: number;
};

type UseCompanyNewsResult = {
  news: GetCompanyNewsResponse;
  isLoading: boolean;
  reload: () => void;
};

const EMPTY_NEWS: GetCompanyNewsResponse = {
  items: [],
  total: 0,
  daily_counts: [],
  company_counts: [],
};

const toKey = (values: string[] | null): string =>
  values && values.length > 0 ? values.join(",") : "";

const fromKey = (key: string): string[] | null =>
  key ? key.split(",") : null;

export const useCompanyNews = ({
  compassCids,
  portfolioIndexIds,
  days,
  publishedRange = null,
  limit,
}: UseCompanyNewsParams): UseCompanyNewsResult => {
  const { addToast } = useToast();
  const [news, setNews] = useState<GetCompanyNewsResponse>(EMPTY_NEWS);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const { sendData } = usePost<
    GetCompanyNewsResponse,
    GetCompanyNewsRequest
  >();
  const companyKey = toKey(compassCids);
  const fundKey = toKey(portfolioIndexIds);
  const publishedFrom = publishedRange?.published_from ?? null;
  const publishedTo = publishedRange?.published_to ?? null;

  const request = useMemo<GetCompanyNewsRequest>(
    () => ({
      compass_cid: fromKey(companyKey),
      portfolio_index_id: fromKey(fundKey),
      days,
      published_range:
        publishedFrom && publishedTo
          ? { published_from: publishedFrom, published_to: publishedTo }
          : null,
      limit,
    }),
    [companyKey, fundKey, days, publishedFrom, publishedTo, limit]
  );

  useEffect(() => {
    let isCurrent = true;
    sendData("GET_COMPANY_NEWS", request).then((response) => {
      if (!isCurrent) {
        return;
      }
      if (!response) {
        addToast(MESSAGES.NEWS_FEED.ERROR, "error");
      }
      setNews(response ?? EMPTY_NEWS);
      setHasLoaded(true);
    });
    return (): void => {
      isCurrent = false;
    };
  }, [sendData, request, reloadCount, addToast]);

  const reload = useCallback((): void => {
    setReloadCount((count) => count + 1);
  }, []);

  return {
    news,
    isLoading: !hasLoaded,
    reload,
  };
};
